/**
 * Mobile-forensics API — the read side of the case comms parquet store.
 *
 * Extractions (one per ingested .mfe device bundle), their conversations and
 * chat-style message pages. Everything is read-only reconstruction; the whole
 * surface 503s when the Mobile Forensics extension is switched off.
 */

import { apiClient } from './client'

export interface CommsDevice {
  device_name?: string
  product_type?: string
  ios_version?: string
  phone_number?: string
}

export interface CommsExtraction {
  id: string
  extractionId: string
  documentId: string | null
  createdAt: string
  tier: string | null
  device: CommsDevice
  counts: Record<string, number>
  report: CommsAppReport[]
}

/** Per-application parse outcome — "no messages" must never read as "did not
 * communicate", so absent/not-obtainable apps are reported explicitly. */
export interface CommsAppReport {
  app: string
  status: 'parsed' | 'absent' | 'schema_mismatch' | 'not_obtainable'
  detail?: string
  messages?: number
  conversations?: number
  calls?: number
  metadata_events?: number
}

export interface CommsLastMessage {
  body: string | null
  sentAt: string | null
  direction: number
  msgType: string
}

export interface CommsConversation {
  conversationId: string
  app: string
  displayName: string | null
  participants: string[]
  messageCount: number
  threadStart: string | null
  threadEnd: string | null
  lastMessage: CommsLastMessage | null
}

export interface CommsMessage {
  messageId: string
  conversationId: string
  sender: string | null
  recipient: string | null
  /** 0 = incoming, 1 = outgoing (the device owner wrote it). */
  direction: number
  sentAt: string | null
  body: string | null
  msgType: string
  replyTo: string | null
  attachmentRef: string | null
  app: string
  origin: string
  /**
   * The file inside the sealed bundle this message was read from, as
   * `chain_of_custody.json` addresses it (`<domain>/<relative path>`). Null for
   * extractions parsed before the parsers recorded it.
   */
  source: string | null
}

export interface CommsMessageMatches {
  matches: CommsMessage[]
  /** The cap was hit — the UI must say so rather than imply this is all of it. */
  hasMore: boolean
}

export interface CommsMessagesPage {
  messages: CommsMessage[]
  hasMore: boolean
}

/** One OS-recorded notification trace (ADR-0018) — evidence that an app was
 * active on the device, never a recovered message. */
export interface CommsNotificationEvent {
  eventId: string
  /** Bundle identifier the OS attributed the record to; null when the store's
   * application map was missing. */
  app: string | null
  /** `notification.pending` (scheduled to fire) or `notification.delivered`
   * (sitting on screen when the backup ran). */
  kind: string
  occurredAt: string | null
  counterpart: string | null
  /** Title · subtitle · body — present only where previews were left enabled. */
  detail: string | null
  /** The file inside the sealed bundle this row was read from. */
  source: string | null
}

export interface CommsNotificationsPage {
  events: CommsNotificationEvent[]
  hasMore: boolean
  /** Apps the OS had registered for notifications — the inventory that survives
   * even for apps whose content is unobtainable. First page only. */
  registered: string[]
  /** Distinct apps behind the timed events — drives the filter chips. First page only. */
  apps: string[]
}

/** A timed visit, or the browser's most-visited ranking (no timestamp). */
export type CommsWebVisitKind = 'visit' | 'top_site'

export interface CommsWebVisit {
  visitId: string
  browser: string
  url: string
  title: string | null
  host: string | null
  visitedAt: string | null
  /** Cumulative per-URL count where the browser tracks it. */
  visitCount: number | null
  /** Null for extractions parsed before the distinction existed — read as a visit. */
  kind: CommsWebVisitKind | null
  /** Position in the most-visited list; set on `top_site` rows only. */
  rank: number | null
  /** The file inside the sealed bundle this row was read from. */
  source: string | null
}

export interface CommsWebVisitsPage {
  visits: CommsWebVisit[]
  hasMore: boolean
  /** Distinct browsers present — drives the tab's filter chips. */
  browsers: string[]
}

/** An inclusive time window the whole view is scoped to; ISO instants. */
export interface CommsRange {
  from?: string
  to?: string
}

export type CommsActivityBucketSize = 'hour' | 'day' | 'week' | 'month'

/** One bar of a thread's activity histogram. */
export interface CommsActivityBucket {
  bucket: string
  n: number
}

/** A promoted range of messages, materialised as one graph node (ADR-0024). */
export interface CommsExcerpt {
  /** Neo4j element id — what the graph view links to. */
  id: string
  /** Stable business key; what the mine/delete endpoints address. */
  excerptId: string
  /** Element id of the anchoring Conversation, so the pair can go on a canvas. */
  conversationNodeId?: string | null
  /** Element id of the EXCERPT_OF edge, so the canvas carries the real edge. */
  excerptOfId?: string | null
  conversationId: string
  commsExtractionId: string
  messageIds: string[]
  tsFrom: string | null
  tsTo: string | null
  messageCount: number
  participants: string[]
  text: string
  note: string
  createdBy: string
}

export interface CommsExcerptCreate {
  conversationId: string
  startMessageId: string
  endMessageId: string
  note?: string
}

export const commsApi = {
  listExtractions(caseId: string): Promise<CommsExtraction[]> {
    return apiClient.get(`/v1/cases/${caseId}/comms/extractions`)
  },

  createExcerpt(
    caseId: string,
    extractionId: string,
    body: CommsExcerptCreate
  ): Promise<CommsExcerpt> {
    return apiClient.post(`/v1/cases/${caseId}/comms/${extractionId}/excerpts`, body)
  },

  mineExcerpt(
    caseId: string,
    extractionId: string,
    excerptId: string
  ): Promise<{ mined: number; node_count?: number }> {
    return apiClient.post(
      `/v1/cases/${caseId}/comms/${extractionId}/excerpts/${encodeURIComponent(excerptId)}/mine`
    )
  },

  listExcerpts(
    caseId: string,
    extractionId: string,
    conversationId: string
  ): Promise<CommsExcerpt[]> {
    return apiClient.get(
      `/v1/cases/${caseId}/comms/${extractionId}/conversations/${encodeURIComponent(conversationId)}/excerpts`
    )
  },

  listConversations(
    caseId: string,
    extractionId: string,
    range: CommsRange = {}
  ): Promise<CommsConversation[]> {
    return apiClient.get(`/v1/cases/${caseId}/comms/${extractionId}/conversations`, {
      params: { from: range.from, to: range.to },
    })
  },

  conversationActivity(
    caseId: string,
    extractionId: string,
    conversationId: string,
    bucket: CommsActivityBucketSize = 'day'
  ): Promise<CommsActivityBucket[]> {
    return apiClient.get(
      `/v1/cases/${caseId}/comms/${extractionId}/conversations/${encodeURIComponent(conversationId)}/activity`,
      { params: { bucket } }
    )
  },

  listMessages(
    caseId: string,
    extractionId: string,
    conversationId: string,
    opts: { before?: string; limit?: number } & CommsRange = {}
  ): Promise<CommsMessagesPage> {
    return apiClient.get(
      `/v1/cases/${caseId}/comms/${extractionId}/conversations/${encodeURIComponent(conversationId)}/messages`,
      { params: { before: opts.before, limit: opts.limit, from: opts.from, to: opts.to } }
    )
  },

  searchMessages(
    caseId: string,
    extractionId: string,
    q: string,
    opts: { limit?: number; fuzzy?: boolean } & CommsRange = {}
  ): Promise<CommsMessageMatches> {
    return apiClient.get(`/v1/cases/${caseId}/comms/${extractionId}/messages/search`, {
      params: { q, limit: opts.limit, fuzzy: opts.fuzzy, from: opts.from, to: opts.to },
    })
  },

  listWebVisits(
    caseId: string,
    extractionId: string,
    opts: { offset?: number; limit?: number } & CommsRange = {}
  ): Promise<CommsWebVisitsPage> {
    return apiClient.get(`/v1/cases/${caseId}/comms/${extractionId}/web-visits`, {
      params: { offset: opts.offset, limit: opts.limit, from: opts.from, to: opts.to },
    })
  },

  listNotifications(
    caseId: string,
    extractionId: string,
    opts: { offset?: number; limit?: number } & CommsRange = {}
  ): Promise<CommsNotificationsPage> {
    return apiClient.get(`/v1/cases/${caseId}/comms/${extractionId}/notifications`, {
      params: { offset: opts.offset, limit: opts.limit, from: opts.from, to: opts.to },
    })
  },
}
